<template>
  <b-modal :visible="visible" title="Informações do Cliente" @hide="onHide">
    <div>
      <!-- Alerta se possui reports -->
      <b-alert
        v-if="hasReports && !showReportForm"
        show
        variant="warning"
        class="mb-3 d-flex align-items-center"
      >
        <i class="bi bi-shield-exclamation me-2" style="font-size: 1.2rem"></i>
        <div class="flex-grow-1">
          <strong>Atenção:</strong>
          Este cliente possui
          <b-badge variant="danger" class="ms-1">
            {{ computedReportsCount }}
          </b-badge>
          {{ computedReportsCount === 1 ? 'report' : 'reports' }} registrado{{
            computedReportsCount === 1 ? '' : 's'
          }}.
        </div>
      </b-alert>

      <p class="info-tip">
        Dica: Toque duas vezes em qualquer informação para copiá-la.
      </p>

      <p>
        <i
          class="bi bi-exclamation-triangle-fill text-warning"
          style="font-size: 2rem"
        ></i>
        <strong class="ms-2">Nome:</strong>
        <span
          @dblclick="copyToClipboard(customerView.name)"
          @touchend="doubleTap('name', customerView.name)"
        >
          {{ customerView.name || 'Sem Informações' }}
        </span>
      </p>

      <p>
        <strong>Email:</strong>
        <span
          @dblclick="copyToClipboard(customerView.email)"
          @touchend="doubleTap('email', customerView.email)"
        >
          {{ customerView.email || 'Sem Informações' }}
        </span>
      </p>

      <p>
        <strong>Telefone:</strong>
        <span
          @dblclick="copyToClipboard(customerView.phone)"
          @touchend="doubleTap('phone', customerView.phone)"
        >
          {{ customerView.phone || 'Sem Informações' }}
        </span>
      </p>

      <p>
        <strong>Documento:</strong>
        <span
          @dblclick="copyToClipboard(customerView.document)"
          @touchend="doubleTap('document', customerView.document)"
        >
          {{ customerView.document || 'Sem Informações' }}
        </span>
      </p>

      <p class="d-flex align-items-center">
        <strong>Pontos Atuais:</strong>
        <span
          class="points-badge ms-2 px-2 py-1 bg-light border"
          @dblclick="copyToClipboard(totalPointsString)"
          @touchend="doubleTap('points', totalPointsString)"
        >
          {{ totalPoints }} / 10
        </span>
      </p>

      <!-- Header da seção de reports + botão -->
      <div class="d-flex align-items-center justify-content-between mb-2">
        <div class="d-flex align-items-center">
          <strong>Reports:</strong>
          <b-badge :variant="hasReports ? 'danger' : 'secondary'" class="ms-2">
            {{ computedReportsCount }}
          </b-badge>
        </div>

        <b-button
          size="sm"
          :variant="showReportForm ? 'outline-danger' : 'outline-dark'"
          @click="toggleReportForm"
        >
          <i class="bi bi-flag me-1"></i>
          {{ showReportForm ? 'Cancelar' : 'Reportar cliente' }}
        </b-button>
      </div>

      <!-- Área dinâmica: OU lista de reports (motivos) OU formulário para novo report -->
      <transition name="fade">
        <!-- LISTA DE REPORTS -->
        <div
          v-if="!showReportForm"
          key="list"
          class="card shadow-sm border-0 mb-2"
        >
          <div class="card-body p-3">
            <!-- Loader inicial -->
            <div
              v-if="loading.bootstrap"
              class="text-muted small d-flex align-items-center"
            >
              <i class="bi bi-arrow-repeat me-2 spin"></i> Carregando reports...
            </div>

            <template v-else>
              <template v-if="hasReports">
                <b-list-group class="reports-list">
                  <b-list-group-item
                    v-for="(r, idx) in localReports"
                    :key="(r && r.id) || idx"
                    class="d-flex align-items-start justify-content-between"
                  >
                    <!-- contador à esquerda -->
                    <div class="me-3 d-flex align-items-start">
                      <b-badge variant="dark" pill class="report-counter">{{
                        idx + 1
                      }}</b-badge>
                    </div>

                    <div class="flex-grow-1 pe-3">
                      <div class="text-break">{{ (r && r.reason) || '—' }}</div>
                      <div class="mt-1 text-muted small">
                        <span v-if="r && r.createdAt"
                          >Em: {{ formatDate(r.createdAt) }}</span
                        >
                        <span v-if="r && r.user && r.user.name">
                          • Usuário: {{ r.user.name }}</span
                        >
                        <span v-if="r && r.store && r.store.name">
                          • Loja: {{ r.store.name }}</span
                        >
                      </div>
                    </div>

                    <div class="ms-2 d-flex align-items-start">
                      <b-button
                        v-if="canDeleteReport(r)"
                        size="sm"
                        variant="outline-danger"
                        title="Excluir report"
                        @click="onDeleteReport(r)"
                      >
                        <span class="fas fa-trash-alt text-danger"></span>
                      </b-button>
                    </div>
                  </b-list-group-item>
                </b-list-group>
              </template>

              <template v-else>
                <div class="text-muted small">
                  Nenhum report registrado para este cliente.
                </div>
              </template>
            </template>
          </div>
        </div>

        <!-- FORMULÁRIO DE NOVO REPORT -->
        <div v-else key="form" class="card shadow-sm border-0 mb-2">
          <div class="card-body p-3">
            <label class="form-label mb-1 fw-semibold">
              Motivo do report
              <small class="text-muted ms-1">(obrigatório)</small>
            </label>

            <b-form-textarea
              v-model.trim="reason"
              :maxlength="reasonMax"
              rows="3"
              placeholder="Descreva rapidamente o que aconteceu…"
            ></b-form-textarea>

            <div class="d-flex justify-content-between align-items-center mt-2">
              <small
                :class="{
                  'text-muted': remainingChars >= 0,
                  'text-danger': remainingChars < 0
                }"
              >
                {{ remainingChars }} caractere{{
                  Math.abs(remainingChars) === 1 ? '' : 's'
                }}
                {{ remainingChars >= 0 ? 'restantes' : 'excedidos' }}
              </small>

              <div class="d-flex gap-2">
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  @click="cancelReportForm"
                >
                  Cancelar
                </b-button>

                <b-button
                  size="sm"
                  variant="danger"
                  :disabled="!canConfirm || loading.create"
                  @click="confirmReport"
                >
                  <span v-if="loading.create">
                    <i class="bi bi-arrow-repeat me-1 spin"></i> Enviando…
                  </span>
                  <span v-else>
                    <i class="bi bi-send me-1"></i> Confirmar envio
                  </span>
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </b-modal>
</template>

<script>
import services from '@/services'
import { mapGetters } from 'vuex'

export default {
  name: 'CustomerInfoModal',
  props: {
    customer: {
      type: Object,
      required: true
    },
    visible: {
      type: Boolean,
      required: true
    },
    // total absoluto (fallback); se "reports" vier, usamos reports.length
    reportsCount: {
      type: Number,
      default: 0
    },
    // lista de reports do cliente (opcional)
    // cada item pode ter { id, reason, createdAt, user?: {name}, store?: {name} }
    reports: {
      type: Array,
      default: () => []
    },
    // necessário para criar o report
    storeId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      lastTouch: {},
      showReportForm: false,
      reason: '',
      reasonMax: 280,

      // dados locais (atualizados via requests)
      localCustomer: null,
      localReports: [],
      loading: {
        bootstrap: false,
        create: false
      }
    }
  },
  computed: {
    ...mapGetters('store', ['isAdmin']),
    // fonte de verdade do cliente
    customerView() {
      return this.localCustomer || this.customer || {}
    },
    totalPoints() {
      return (
        (this.customerView.points &&
          this.customerView.points.total_rents_closed) ||
        0
      )
    },
    totalPointsString() {
      return this.totalPoints.toString()
    },

    // >>> NOVO: fonte única de verdade para render dos reports
    reportsView() {
      return this.localReports && this.localReports.length
        ? this.localReports
        : this.reports || []
    },
    computedReportsCount() {
      return this.reportsView.length || this.reportsCount || 0
    },
    hasReports() {
      return this.computedReportsCount > 0
    },
    remainingChars() {
      return this.reasonMax - (this.reason?.length || 0)
    },
    canConfirm() {
      return (
        this.reason && this.reason.trim().length > 0 && this.remainingChars >= 0
      )
    }
  },
  watch: {
    // Abriu o modal: refaz bootstrap
    visible(val) {
      if (val) this.bootstrapRequests()
    },
    // Mudou o cliente recebido: refaz bootstrap se o modal estiver aberto
    customer: {
      deep: true,
      handler() {
        if (this.visible) this.bootstrapRequests()
      }
    }
  },
  mounted() {
    if (this.visible) this.bootstrapRequests()
  },
  methods: {
    canDeleteReport(report) {
      if (!report) return false

      // Admin sempre pode
      if (this.isAdmin) return true

      // Não-admin: apenas nos primeiros 5 minutos do report
      const created = new Date(report.createdAt).getTime()
      if (!Number.isFinite(created)) return false

      const FIVE_MIN = 5 * 60 * 1000 // 5 minutos em ms
      const now = Date.now()
      return now - created <= FIVE_MIN
    },
    async onDeleteReport(report) {
      if (!this.canDeleteReport(report)) {
        this.$root.$toast?.error?.(
          'Você não tem permissão para excluir este report.'
        )
        return
      }
      if (!report?.id) return
      const message = `Deseja realmente excluir o report ?`
      const result = await this.$bvModal.msgBoxConfirm(message, {
        title: 'Confirmação',
        size: 'sm',
        okVariant: 'danger',
        okTitle: 'Sim',
        cancelTitle: 'Não',
        centered: true
      })
      if (!result) {
        return
      }

      try {
        await services.customers.deleteCustomerReport(report.id)
        this.$root.$toast?.success?.('Report excluído.')
        // recarrega a lista
        const customerId = this.localCustomer?.id || this.customer?.id
        const { data } =
          await services.customers.fetchCustomerReportsByCustomerId(customerId)
        this.localReports = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : []
      } catch (err) {
        this.$root.$toast?.error?.('Não foi possível excluir o report.')
        // eslint-disable-next-line no-console
        console.error(err)
      }
    },
    async bootstrapRequests() {
      try {
        this.loading.bootstrap = true
        this.localReports = []
        this.localCustomer = null

        // Atualiza cliente pelo documento, se existir
        const doc = this.customer?.document
        if (doc) {
          try {
            const { data } = await services.customers.fetchCustomerByDocument(
              doc
            )
            // Ajuste conforme seu backend (data?.data ou data direto)
            this.localCustomer = data?.data || data || this.customer
          } catch {
            this.localCustomer = this.customer
          }
        } else {
          this.localCustomer = this.customer
        }

        // Buscar reports pelo customerId (se tivermos id)
        const customerId = this.localCustomer?.id || this.customer?.id
        if (customerId) {
          const { data } =
            await services.customers.fetchCustomerReportsByCustomerId(
              customerId
            )
          // Backend: { success, data: [...] }
          this.localReports = Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data)
            ? data
            : []
        }
      } catch (err) {
        this.$root.$toast?.error?.('Não foi possível carregar os reports.')
        // eslint-disable-next-line no-console
        console.error(err)
      } finally {
        this.loading.bootstrap = false
      }
    },

    onHide() {
      this.$emit('close')
      this.resetReportForm()
    },
    copyToClipboard(text) {
      if (!text) {
        this.$root.$toast?.error?.('Sem informações para copiar.')
        return
      }
      navigator.clipboard
        .writeText(String(text))
        .then(() => {
          this.$root.$toast?.success?.('Informação copiada com sucesso!')
        })
        .catch((err) => {
          this.$root.$toast?.error?.('Ocorreu um problema ao copiar.')
          // eslint-disable-next-line no-console
          console.log(err)
        })
    },
    doubleTap(key, text) {
      const now = Date.now()
      if (this.lastTouch[key] && now - this.lastTouch[key] < 400) {
        this.copyToClipboard(text)
        this.lastTouch[key] = 0
      } else {
        this.lastTouch[key] = now
      }
    },
    toggleReportForm() {
      this.showReportForm = !this.showReportForm
      if (!this.showReportForm) this.resetReportForm()
    },
    cancelReportForm() {
      this.resetReportForm()
      this.showReportForm = false
    },
    resetReportForm() {
      this.reason = ''
    },

    async confirmReport() {
      if (!this.canConfirm) return
      try {
        this.loading.create = true

        const customerId = this.localCustomer?.id || this.customer?.id
        if (!customerId) {
          this.$root.$toast?.error?.('Cliente inválido.')
          return
        }

        await services.customers.createCustomerReport({
          storeId: this.storeId,
          customerId,
          reason: this.reason.trim()
        })

        this.$root.$toast?.success?.('Report enviado com sucesso.')

        // Atualiza a lista de reports após criar
        const { data } =
          await services.customers.fetchCustomerReportsByCustomerId(customerId)
        this.localReports = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : []

        // Reseta form e volta para a lista
        this.resetReportForm()
        this.showReportForm = false
      } catch (err) {
        this.$root.$toast?.error?.('Não foi possível enviar o report.')
        // eslint-disable-next-line no-console
        console.error(err)
      } finally {
        this.loading.create = false
      }
    },

    formatDate(dateStr) {
      try {
        const d = new Date(dateStr)
        return d.toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch {
        return String(dateStr || '')
      }
    }
  }
}
</script>

<style scoped>
.info-tip {
  font-size: 0.9em;
  color: #6c757d;
  margin-bottom: 16px;
}

span {
  cursor: pointer;
  transition: color 0.2s;
}

span:hover {
  color: #007bff;
  text-decoration: underline;
}

.points-badge {
  display: inline-block;
  border-radius: 4px;
  margin-left: 4px;
}

/* Lista de reports com altura máxima e scroll */
.reports-list {
  max-height: 220px;
  overflow: auto;
}

/* Transição suave para trocar entre lista e form */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Spinner simples usando bootstrap icons */
.spin {
  display: inline-block;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Card estilo do mock */
.report-item {
  background: #f8f8f8; /* fundo claro, não preto */
  color: #4d4d4d; /* texto escuro */
  border: 2px solid #cacaca; /* borda escura estilo “giz” */
  border-radius: 12px;
  padding: 14px 16px;
}

/* Título central [Motivo] */
.report-item__header {
  text-align: center;
  font-weight: 700;
  margin-bottom: 6px;
  font-size: 1rem;
}

/* Linha divisória */
.report-item__divider {
  height: 2px;
  background: #222;
  width: 90%;
  margin: 0 auto 10px auto;
}

/* Texto “Motivo” centralizado */
.report-item__reason {
  text-align: center;
  font-size: 1rem;
  margin-bottom: 12px;
  font-weight: 500;
  word-break: break-word;
}

/* Lista Data / Onde / Usuario */
.report-item__meta li + li {
  margin-top: 4px;
}
.report-item__label {
  font-weight: 600;
  margin-right: 4px;
}
.report-item__value {
  color: #333;
}
</style>
